import { useEffect, type Dispatch, type SetStateAction } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField, MenuItem } from "@mui/material";
import { getLoanPurposeTypes } from "@/constants";
import type { ApplicationFormSlice, LoanDetails, ApplicationState } from "@/store/slices/types/applicationFormSlice";
import {
  updateLoanDetails,
  loanDetailsSelector,
  saveLoanDetails,
  applicationSelector,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";

interface LoanPurposeProps {
  quoteValues: Record<string, string>;
  setQuoteValue: Dispatch<SetStateAction<Record<string, string>>>;
  disabled?: boolean;
}

const LoanPurpose = ({ quoteValues, setQuoteValue, disabled }: LoanPurposeProps) => {
  const dispatch = useDispatch();
  const { purpose, _id } = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const { applicationType, _id: applicationId } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const loanPurposeTypes = getLoanPurposeTypes[applicationType as keyof typeof getLoanPurposeTypes] ?? [];

  const onChange = (value: string, fieldName: string) => {
    if (value) {
      setQuoteValue({ ...quoteValues, [fieldName]: value });
    }
  };

  const onBlur = async (value: string, fieldName: string) => {
    try {
      dispatch(saveLoanDetails({ [fieldName]: value }));
      if (_id && value !== purpose) {
        dispatch(updateLoanDetails({ applicationId, _id, [fieldName]: value }));
      }
    } catch (error) {
      // TODO: Absorb error for now, will think of a generic handler for field errors in the application form.
    }
  };

  // Set loan purpose saved in loan details into local state.
  useEffect(() => {
    if (purpose) {
      setQuoteValue((prevValues) => {
        return { ...prevValues, purpose };
      });
    }
  }, [purpose]);

  return (
    <TextField
      fullWidth
      select
      id="purpose"
      label="Loan purpose"
      variant="filled"
      type="text"
      size="small"
      name="purpose"
      value={quoteValues.purpose || ""}
      onChange={(event) => onChange(event?.target?.value, "purpose")}
      onBlur={(event) => onBlur(event?.target?.value, "purpose")}
      disabled={disabled}
    >
      {loanPurposeTypes.map((purpose) => {
        return (
          <MenuItem value={purpose} key={purpose}>
            {purpose}
          </MenuItem>
        );
      })}
    </TextField>
  );
};

export { LoanPurpose };
